import { useState, useEffect, useMemo } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Combobox } from './ui/combobox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Loader2, ArrowRight, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useDimensionsList } from '../hooks/useDimension';
import { useAnalyses } from '../hooks/useAnalyses';
import type { DashboardComponent } from '../services/dashboard.service';

type ComponentType = 'MARKDOWN' | 'FILTER' | 'ANALYSIS';

interface ComponentConfigSheetProps {
  component: DashboardComponent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (component: DashboardComponent) => void;
  onDelete?: (componentId: string) => void;
}

const COMPONENT_TYPE_OPTIONS = [
  { value: 'ANALYSIS', label: 'Chart' },
  { value: 'MARKDOWN', label: 'Markdown' },
  { value: 'FILTER', label: 'Filter' },
];

export function ComponentConfigSheet({
  component,
  open,
  onOpenChange,
  onSave,
  onDelete,
}: ComponentConfigSheetProps) {
  // Local state for editing
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [objectType, setObjectType] = useState<ComponentType>('ANALYSIS');
  const [objectId, setObjectId] = useState<string | undefined>(undefined);
  const [markdownContent, setMarkdownContent] = useState('');
  const [dimensionId, setDimensionId] = useState<string | undefined>(undefined);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [ignoreDateRange, setIgnoreDateRange] = useState(false);
  const [hideHeader, setHideHeader] = useState(false);
  const [hideToolbar, setHideToolbar] = useState(false);
  const [disableSort, setDisableSort] = useState(false);
  const [inputLabel, setInputLabel] = useState('');
  const [overridesOpen, setOverridesOpen] = useState(false);

  // Fetch dimensions and analyses for comboboxes
  const { data: dimensionsData, isLoading: dimensionsLoading } = useDimensionsList({
    pageSize: 500,
    enabled: open && objectType === 'FILTER',
  });
  const { data: analysesData, isLoading: analysesLoading } = useAnalyses({
    pageSize: 500,
    enabled: open && objectType === 'ANALYSIS',
  });

  // Reset form when component changes
  useEffect(() => {
    if (component) {
      setTitle(component.title || '');
      setDescription(component.description || '');
      setObjectType((component.object_type as ComponentType) || 'ANALYSIS');
      setObjectId(component.object_id);
      setMarkdownContent(component.markdown_content || '');
      setDimensionId(component.filter_config?.dimension_id);
      setAllowMultiple(component.filter_config?.allow_multiple || false);
      setIgnoreDateRange(component.ignore_date_range || false);
      setHideHeader(component.hide_header || false);
      setHideToolbar(component.hide_toolbar || false);
      setDisableSort(component.disable_sort || false);
      setInputLabel(component.filter_config?.input_label || '');
      setOverridesOpen(!!(component.title || component.description));
    } else {
      // Reset to defaults for new component
      setTitle('');
      setDescription('');
      setObjectType('ANALYSIS');
      setObjectId(undefined);
      setMarkdownContent('');
      setDimensionId(undefined);
      setAllowMultiple(false);
      setIgnoreDateRange(false);
      setHideHeader(false);
      setHideToolbar(false);
      setDisableSort(false);
      setInputLabel('');
      setOverridesOpen(false);
    }
  }, [component, open]);

  // Build options for comboboxes
  const dimensionOptions = useMemo(() => {
    if (!dimensionsData?.records) return [];
    return dimensionsData.records
      .map((d) => ({
        value: d.id,
        label: d.name || d.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [dimensionsData]);

  const analysisOptions = useMemo(() => {
    if (!analysesData?.records) return [];
    return analysesData.records
      .map((a) => ({
        value: a.id,
        label: a.name || a.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [analysesData]);

  const handleSave = () => {
    if (!component) return;

    const updatedComponent: DashboardComponent = {
      ...component,
      title,
      description,
      object_type: objectType,
      object_id: objectType === 'ANALYSIS' ? objectId : undefined,
      markdown_content: objectType === 'MARKDOWN' ? markdownContent : undefined,
      filter_config:
        objectType === 'FILTER'
          ? {
              filter_type: 'DIMENSION',
              dimension_id: dimensionId,
              allow_multiple: allowMultiple,
              comparator: 'IN',
              input_label: inputLabel || undefined,
            }
          : undefined,
      ignore_date_range: objectType === 'ANALYSIS' ? ignoreDateRange : undefined,
      hide_header: hideHeader,
      hide_toolbar: objectType === 'ANALYSIS' ? hideToolbar : undefined,
      disable_sort: objectType === 'ANALYSIS' ? disableSort : undefined,
    };

    onSave(updatedComponent);
    onOpenChange(false);
  };

  const handleDelete = () => {
    if (component && onDelete) {
      onDelete(component.id);
      onOpenChange(false);
    }
  };

  const isLoading =
    (objectType === 'FILTER' && dimensionsLoading) ||
    (objectType === 'ANALYSIS' && analysesLoading);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Configure Component</SheetTitle>
          <SheetDescription>
            Set up the component's type and configuration.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Component Type */}
          <div className="space-y-2">
            <Label>Component Type</Label>
            <Combobox
              options={COMPONENT_TYPE_OPTIONS}
              value={objectType}
              onValueChange={(value) => setObjectType(value as ComponentType)}
              placeholder="Select type..."
            />
          </div>

          {/* Type-specific configuration */}
          {objectType === 'MARKDOWN' && (
            <div className="space-y-2">
              <Label htmlFor="markdown">Markdown Content</Label>
              <Textarea
                id="markdown"
                value={markdownContent}
                onChange={(e) => setMarkdownContent(e.target.value)}
                placeholder="Enter markdown content..."
                rows={8}
              />
            </div>
          )}

          {objectType === 'FILTER' && (
            <>
              <div className="space-y-2">
                <Label>Dimension</Label>
                {dimensionsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading dimensions...
                  </div>
                ) : (
                  <Combobox
                    options={dimensionOptions}
                    value={dimensionId}
                    onValueChange={setDimensionId}
                    placeholder="Select dimension..."
                    emptyText="No dimensions found"
                  />
                )}
                {dimensionId && (
                  <Link
                    to={`/dimensions/${dimensionId}`}
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                    style={{ cursor: 'pointer' }}
                  >
                    <ArrowRight className="h-3 w-3" />
                    Go to {dimensionOptions.find((d) => d.value === dimensionId)?.label || 'dimension'}
                  </Link>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="input-label">Input Label</Label>
                <Input
                  id="input-label"
                  value={inputLabel}
                  onChange={(e) => setInputLabel(e.target.value)}
                  placeholder="Label shown above the filter input"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="allow-multiple">Allow multiple selections</Label>
                <Switch
                  id="allow-multiple"
                  checked={allowMultiple}
                  onCheckedChange={setAllowMultiple}
                />
              </div>
            </>
          )}

          {objectType === 'ANALYSIS' && (
            <>
              <div className="space-y-2">
                <Label>Chart</Label>
                {analysesLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading charts...
                  </div>
                ) : (
                  <Combobox
                    options={analysisOptions}
                    value={objectId}
                    onValueChange={setObjectId}
                    placeholder="Select chart..."
                    emptyText="No charts found"
                  />
                )}
                {objectId && (
                  <Link
                    to={`/analytics/charts/${objectId}`}
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                    style={{ cursor: 'pointer' }}
                  >
                    <ArrowRight className="h-3 w-3" />
                    Go to {analysisOptions.find((a) => a.value === objectId)?.label || 'chart'}
                  </Link>
                )}
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="ignore-date-range">Ignore dashboard date range</Label>
                <Switch
                  id="ignore-date-range"
                  checked={ignoreDateRange}
                  onCheckedChange={setIgnoreDateRange}
                />
              </div>
            </>
          )}

          {/* Custom Title & Description overrides */}
          <Collapsible open={overridesOpen} onOpenChange={setOverridesOpen}>
            <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              <ChevronRight className={`h-4 w-4 transition-transform ${overridesOpen ? 'rotate-90' : ''}`} />
              Custom Title & Description
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-3 space-y-4">
              <p className="text-xs text-muted-foreground">
                Override the title and description inherited from the linked chart or dimension.
              </p>
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Leave blank to use default"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Leave blank to use default"
                />
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Display options */}
          <div className="pt-4 border-t space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="hide-header">Hide header</Label>
              <Switch
                id="hide-header"
                checked={hideHeader}
                onCheckedChange={setHideHeader}
              />
            </div>
            {objectType === 'ANALYSIS' && (
              <>
                <div className="flex items-center justify-between">
                  <Label htmlFor="hide-toolbar">Hide toolbar</Label>
                  <Switch
                    id="hide-toolbar"
                    checked={hideToolbar}
                    onCheckedChange={setHideToolbar}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="disable-sort">Disable sort</Label>
                  <Switch
                    id="disable-sort"
                    checked={disableSort}
                    onCheckedChange={setDisableSort}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <SheetFooter className="mt-8">
          <div className="flex w-full justify-between">
            {onDelete && component && (
              <Button variant="destructive" onClick={handleDelete}>
                Delete
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isLoading}>
                Save
              </Button>
            </div>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
